import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { shallow } from 'zustand/shallow';

import { JoyajoyImageFile, LoadingSpinner } from '../../components/Molecules';
import { WebStoreReferralRewardTier2Template } from '../../components/Templates';
import useFetchList, { FetchListFn } from '../../hooks/useFetchList';
import useLocale from '../../hooks/useLocale';
import useRedirectEntry from '../../hooks/useRedirectEntry';
import useRedirectHome from '../../hooks/useRedirectHome';
import {
  ReferralSystem,
  RewardRedemptionTabState,
  RewardReferralRole,
} from '../../models/RewardStore';
import {
  useRewardStore,
  useRewardStoreListField,
} from '../../stores/RewardStore';
import { useSessionStore } from '../../stores/SessionStore';
import { useUserStore } from '../../stores/UserStore';
import { arrayEqual } from '../../utils/array';

type State = {
  loadingTotal: number;
  finishRewardReferral: boolean;
  finishCustomerRewardReferral: boolean;
};

const { fetchRewardReferral, fetchRewardReferralUser } =
  useRewardStore.getState();

const WebStoreReferralRewardTier2Screen = () => {
  const l = useLocale();
  // const { distributorId, customerId1, customerId2 } = useParams();
  const { customerId2 } = useParams();

  const userId = useSessionStore((store) => store.activeUserId);

  const userProfile = useUserStore((store) => store.getProfile(userId ?? ''));

  const currency = useMemo(() => {
    return userProfile?.localCurrency ?? 'USD';
  }, [userProfile]);

  const [state, setState] = useState<State>({
    loadingTotal: 0,
    finishRewardReferral: false,
    finishCustomerRewardReferral: false,
  });

  useRedirectEntry();

  useRedirectHome(!customerId2);

  useEffect(() => {
    if (userId) {
      setState((s) => ({ ...s, loadingTotal: s.loadingTotal + 1 }));
      fetchRewardReferral({ referralSystem: ReferralSystem.Joyajoy })
        .catch(console.error)
        .finally(() => {
          setState((s) => ({
            ...s,
            loadingTotal: s.loadingTotal - 1,
            finishRewardReferral: true,
          }));
        });
    }
  }, [userId]);

  const userRewardReferral = useRewardStore(
    (store) => store.getRewardReferral(`${ReferralSystem.Joyajoy}_${userId}`),
    shallow,
  );

  useRedirectHome(!userRewardReferral && state.finishRewardReferral);

  useEffect(() => {
    if (customerId2) {
      setState((s) => ({ ...s, loadingTotal: s.loadingTotal + 1 }));
      fetchRewardReferralUser(ReferralSystem.Joyajoy, customerId2)
        .catch(console.error)
        .finally(() => {
          setState((s) => ({
            ...s,
            loadingTotal: s.loadingTotal - 1,
            finishCustomerRewardReferral: true,
          }));
        });
    }
  }, [customerId2, userId]);

  const customerRewardReferral = useRewardStore(
    (store) =>
      store.getRewardReferral(`${ReferralSystem.Joyajoy}_${customerId2}`),
    shallow,
  );

  const customerUserProfile = useUserStore((store) =>
    store.getProfile(customerId2 ?? ''),
  );

  useRedirectHome(
    !customerRewardReferral && state.finishCustomerRewardReferral,
  );

  const rewardOfferStore = 'system';
  const rewardRedemptionsFieldName = `${rewardOfferStore}_${RewardRedemptionTabState.Completed}_${customerId2}`;
  const rewardRedemptionsField = useRewardStoreListField(
    'redemptions',
    rewardRedemptionsFieldName,
  );

  const fetchRewardRedemptions = useCallback<FetchListFn>(
    (cursor) =>
      useRewardStore
        .getState()
        .fetchRedemptionsByRedeemedBy(
          rewardOfferStore,
          RewardRedemptionTabState.Completed,
          customerId2 ?? '',
          ReferralSystem.Joyajoy,
          cursor,
        ),
    [customerId2],
  );

  const {
    fetchMore: fetchMoreRewardRedemptions,
    refresh: refreshRewardRedemptions,
    hasCursor: hasShowMore,
  } = useFetchList(rewardRedemptionsField, fetchRewardRedemptions);

  useEffect(() => {
    if (userId && customerId2) {
      setState((s) => ({ ...s, loadingTotal: s.loadingTotal + 1 }));
      refreshRewardRedemptions()
        .catch(console.error)
        .finally(() => {
          setState((s) => ({ ...s, loadingTotal: s.loadingTotal - 1 }));
        });
    }
  }, [refreshRewardRedemptions, customerId2, userId]);

  const onClickShowMore = useCallback(() => {
    setState((s) => ({ ...s, loadingTotal: s.loadingTotal + 1 }));
    fetchMoreRewardRedemptions()
      .catch(console.error)
      .finally(() => {
        setState((s) => ({ ...s, loadingTotal: s.loadingTotal - 1 }));
      });
  }, [fetchMoreRewardRedemptions]);

  const rewardRedemptions = useRewardStore(
    (store) => store.getRedemptions(rewardRedemptionsFieldName),
    arrayEqual(shallow),
  );

  const purchasedProduct = useMemo(() => {
    return rewardRedemptions.map((redemption) => {
      const rewardOffer = useRewardStore
        .getState()
        .getOffer(redemption.rewardOfferId);
      const i18nRewardOfferInfo = l(rewardOffer?.i18nRewardOfferInfo);

      const firstImage: JoyajoyImageFile = {
        resource: {
          uri:
            i18nRewardOfferInfo?.carousel[0]?.image?.publicUrls?.medium ?? '',
        },
      };

      const confirmReward = redemption
        .getConfirmReward(
          userRewardReferral?.role ?? RewardReferralRole.Customer,
          true,
        )
        ?.convertToCurrency(currency);

      return {
        name: i18nRewardOfferInfo?.name ?? '',
        image: firstImage,
        purchasedAt: redemption.createdAt,
        confirmedReward: confirmReward
          ? `${currency}${confirmReward}`
          : undefined,
        isManagementReward:
          userRewardReferral?.role === RewardReferralRole.Partner,
      };
    });
  }, [currency, l, rewardRedemptions, userRewardReferral?.role]);

  return (
    <>
      {state.loadingTotal ? (
        <LoadingSpinner loading={!!state.loadingTotal} />
      ) : null}
      <WebStoreReferralRewardTier2Template
        profile={customerUserProfile}
        rewardReferralRole={userRewardReferral?.role}
        purchasedProduct={purchasedProduct}
        hasShowMore={hasShowMore}
        onClickShowMore={onClickShowMore}
      />
    </>
  );
};

export default WebStoreReferralRewardTier2Screen;
